/**
 * 애드콘(다우 비즈애드콘) API 발송 — 상품 연결(매핑).
 * 상품(선택지 GiftChoiceUnit / 단일 쿠폰 원상품 GiftItemInfo)에는 두 가지만 둔다:
 *  - deliveryChannel: 이 상품의 기본 발송 매체. 'Addcon'일 때만 API 발송 대상. 없으면 수동(오피스콘 등).
 *  - addcon.noReq: 애드콘 계약번호(발행 NO_REQ). 상품명·단가·유효기간은 계약이 정하므로 저장하지 않는다.
 * 스펙: docs/gift-coupon-addcon-deliver-spec.md §1
 */
export type AddconMappingTarget = 'choice-unit' | 'item-info';

export interface AddconProductMapping {
  noReq: string;
}

/** 상품 쪽 발송 매체 — 쿠폰 gifticon.deliveryChannel(DeliveryChannel enum)과 같은 문자열. undefined = 수동 */
export type GiftProductDeliveryChannel = 'Addcon' | 'Officecon' | 'GiftiShow' | 'EumGift';
export const GIFT_PRODUCT_DELIVERY_CHANNELS: GiftProductDeliveryChannel[] = [
  'Addcon',
  'Officecon',
  'GiftiShow',
  'EumGift',
];

/** 중개서버 계약 상품(백엔드 정규화본) */
export interface AddconGoods {
  noReq: string;
  goodsName?: string;
  companyName?: string;
  nmReq?: string;
  /** null = 업체 지정(10004008) */
  sendType: '1001' | '1002' | '1004' | '2100' | null;
  rawSendType?: string;
  validType?: string;
  validTerm?: number;
  validStart?: string;
  validEnd?: string;
  cpnPrice?: number;
  goodsImage?: string;
  category?: string;
  regDate?: string;
}

export interface AddconEnvironment {
  configured?: boolean;
  baseUrl?: string;
  isDevServer?: boolean;
  messageDelivery?: 'none' | 'real';
  /** 백엔드 안내 문구 (테스트 페이지 /config 쪽만 environmentNote) */
  note?: string;
  /** 다우 웹훅 푸시 수신(POST /webhooks/gifticon, 발신 IP 인증) 설정 — 구서버엔 없음. 스펙 §5-3 */
  webhookPush?: {
    configured?: boolean;
    path?: string;
  };
}

/**
 * PUT /mapping 본문 — 둘 다 선택이지만 하나는 있어야 한다. null = 삭제, 생략 = 그대로.
 * 서버 가드: 'Addcon'인데 계약번호(본문·기존값) 없으면 400, 기존 채널 Addcon인데 noReq:null만 보내면 400.
 */
export interface AddconMappingUpdate {
  deliveryChannel?: GiftProductDeliveryChannel | null;
  noReq?: string | null;
}

export interface AddconMappingResult {
  target: AddconMappingTarget;
  id: string;
  deliveryChannel: GiftProductDeliveryChannel | null;
  addcon: AddconProductMapping | null;
  /** noReq를 보냈을 때만 채워진다 */
  goods?: AddconGoods | null;
}

// ---------------------------------------------------------------------------
// 발행 기록 (giftCouponAddconIssues) — 스펙 §2·§4. 날짜는 인터페이스상 string이지만
// axios 응답 리바이버가 ISO 문자열을 Date로 바꾸므로 런타임은 Date일 수 있다(string 메서드 금지).
// ---------------------------------------------------------------------------
export type IsoLike = string | Date;

/** 발행 기록 상태 (= coupon.addcon.state) */
export type AddconIssueState = 'Pending' | 'Issued' | 'Uncertain' | 'Failed' | 'Canceled';
export const ADDCON_ISSUE_STATES: AddconIssueState[] = [
  'Pending',
  'Issued',
  'Uncertain',
  'Failed',
  'Canceled',
];
export const ADDCON_ISSUE_STATE_LABEL: Record<AddconIssueState, string> = {
  Pending: '호출 중',
  Issued: '발행됨',
  Uncertain: '미확정',
  Failed: '실패',
  Canceled: '발행취소',
};

/** GET /attention 항목의 사유 */
export type AddconAttentionKind =
  | 'UNCERTAIN'
  | 'STALE_PENDING'
  | 'ISSUED_NOT_DELIVERED'
  | 'MESSAGE_FAILED';
export const ADDCON_ATTENTION_LABEL: Record<AddconAttentionKind, string> = {
  UNCERTAIN: '발행 미확정',
  STALE_PENDING: '호출 5분 초과',
  ISSUED_NOT_DELIVERED: '발행됐는데 발송완료 아님',
  MESSAGE_FAILED: '문자 전달 실패',
};

/** 다우 CPN_STATUS */
export const ADDCON_CPN_STATUS_LABEL: Record<string, string> = {
  '00': '미사용',
  '01': '사용됨',
  '02': '취소됨',
  '03': '사용 중',
};

export interface AddconIssueMessage {
  webhookId?: string;
  receivedAt?: IsoLike;
  checkedAt?: IsoLike;
  sendType?: string;
  resultCode?: string;
  resultMsg?: string;
  ok?: boolean;
}

export interface AddconIssueHistoryEntry {
  at: IsoLike;
  type: string;
  by?: string;
  noCpn?: string;
  tsId?: string;
  httpStatus?: number;
  requestId?: string;
  rt?: string;
  rtMsg?: string;
  errorCode?: string;
  cpnStatus?: string;
  note?: string;
}

/** coupon.addcon 요약 */
export interface AddconCouponSummary {
  state: AddconIssueState;
  cpnStatus?: string;
  attempts: number;
  issue?: string;
}

/** 발행 기록에 populate된 쿠폰(일부 필드) */
export interface AddconIssueCouponRef {
  _id: string;
  code?: string;
  seq?: number;
  fullCode?: string;
  redeemStatus?: string;
  giftItem?: unknown;
  selectedChoiceUnit?: unknown;
  recipient?: { phoneNumber?: string; name?: string };
  gifticon?: { deliveredAt?: IsoLike; deliveryChannel?: string };
  addcon?: AddconCouponSummary;
}

/** 발행 시도 1번 = 문서 1건 */
export interface AddconIssueRecord {
  _id: string;
  coupon: string | AddconIssueCouponRef;
  attempt: number;
  state: AddconIssueState;
  noReq: string;
  goodsName?: string;
  cooperOrder: string;
  requestedAt: IsoLike;
  requestedBy?: string;
  issueId?: string;
  noCpn?: string;
  tsId?: string;
  issuedAt?: IsoLike;
  cpnPrice?: number | string;
  cpnStart?: string;
  cpnEnd?: string;
  cpnStatus?: string;
  useDate?: string;
  statusCheckedAt?: IsoLike;
  verifiedAt?: IsoLike;
  engineId?: string;
  realMessageDelivery?: boolean;
  lastError?: { code?: string; message?: string; source?: string } | string | null;
  message?: AddconIssueMessage;
  history?: AddconIssueHistoryEntry[];
  createdAt?: IsoLike;
  updatedAt?: IsoLike;
  /** GET /attention 항목에만 */
  attention?: AddconAttentionKind;
}

export interface AddconCouponIssuesResponse {
  coupon: { fullCode?: string; redeemStatus?: string; addcon?: AddconCouponSummary };
  issues: AddconIssueRecord[];
  labels?: Record<string, string>;
}

export interface AddconConfirmResult {
  verdict: 'issued' | 'not-issued' | 'unknown';
  note?: string;
  delivered?: boolean;
}

/** POST /coupons/:id/resolve — 운영자 판정. issued면 noCpn 필수 */
export interface AddconResolveBody {
  outcome: 'issued' | 'not-issued';
  noCpn?: string;
  tsId?: string;
  note?: string;
}

export interface AddconCancelByOrderResult {
  canceled: boolean;
  gateway?: unknown;
}

export interface AddconRetryDeliverResult {
  delivered: boolean;
  note?: string;
}

// ---------------------------------------------------------------------------
// 중개서버 전체 발행 목록 (대사용) — gifticon-api 요청 5(docs/gift-coupon-addcon-gateway-request.md)
// 백엔드 프록시 GET /gateway-issues 가 정규화해 준다. 미반영이면 404.
// ---------------------------------------------------------------------------
/** 중개서버 OperationStatus 실제 값(main 2fb484f). UNCERTAIN 없음 — 미확정은 UNKNOWN */
export type AddconGatewayIssueStatus = 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'UNKNOWN';
export const ADDCON_GATEWAY_STATUS_LABEL: Record<AddconGatewayIssueStatus, string> = {
  PROCESSING: '처리 중',
  SUCCEEDED: '발행됨',
  FAILED: '실패',
  UNKNOWN: '발행 미확정',
};

export interface AddconGatewayIssueItem {
  id: string;
  issueId?: string;
  cooperOrder?: string;
  couponNumber?: string;
  couponStatus?: string;
  status?: AddconGatewayIssueStatus | string;
  responseStatus?: AddconGatewayIssueStatus | string | null;
  noReq?: string;
  rcvCtn?: string;
  callCtn?: string;
  sendType?: string;
  title?: string;
  tsId?: string;
  goodsName?: string;
  cpnPrice?: string | number;
  cpnStart?: string;
  cpnEnd?: string;
  error?: { source?: string; code?: string; message?: string } | null;
  createdAt?: IsoLike;
  updatedAt?: IsoLike;
}

export interface AddconGatewayIssueList {
  items: AddconGatewayIssueItem[];
  next: string | null;
  hasMore: boolean;
  /** 기간·필터 전체 건수(선택). 취소 건수는 없다 — couponStatus 02 필터나 우리 기록 Canceled로 본다 */
  summary?: Partial<
    Record<'total' | 'succeeded' | 'failed' | 'unknown' | 'processing', number>
  >;
}

export interface AddconGatewayIssueQuery {
  from?: string;
  to?: string;
  after?: string;
  limit?: number;
  status?: string;
}
